import {ESPLoader, type Transport, type FlashSizeValues} from 'esptool-js';
import {md5} from 'js-md5';
import {readEsp32Flash} from './esp32-flash-read';
import {Esp32FirmwareReset, Esp32LoaderTransport} from './esp32-loader-transport';

export interface FirmwareFile {
  name: string;
  offset: number;
  size: number;
  sha256: string;
  url: string;
}

export interface FirmwareRelease {
  id: string;
  name: string;
  version: string;
  builtAt: string;
  chipFamily: 'ESP32';
  flashSizeBytes: number;
  flashMode: 'dio';
  flashFrequency: '40m';
  partitionScheme: 'huge_app' | 'ph_recovery';
  preserveNvs: true;
  notes: string[];
  files: FirmwareFile[];
}

export type FirmwareFlashFile = FirmwareFile & {data: Uint8Array};

// Web Serial is not included in TypeScript's DOM library. Keep the small
// structural surface used here explicit instead of relying on ambient globals.
export interface Esp32SerialPort {
  readonly readable: ReadableStream<Uint8Array> | null;
  readonly writable: WritableStream<Uint8Array> | null;
  open(options: {baudRate: number; bufferSize?: number}): Promise<void>;
  close(): Promise<void>;
  setSignals(signals: {dataTerminalReady?: boolean; requestToSend?: boolean}): Promise<void>;
  getInfo(): {usbVendorId?: number; usbProductId?: number};
}

export interface Esp32Callbacks {
  onProgress?: (percent: number) => void;
  onStatus?: (text: string) => void;
  onLog?: (text: string) => void;
}

export interface Esp32ProvisionConfig {
  ssid: string;
  password: string;
  token: string;
  firmwareVersion?: string;
}

export class Esp32PartitionMismatchError extends Error {
  constructor(readonly migrationSupported = false) {
    super(migrationSupported ? 'Trạm cần nâng cấp một lần để bật cập nhật qua Wi-Fi. Sao lưu trước; Wi-Fi, mã trạm và nhật ký sẽ được giữ.' : 'ESP32 có bảng phân vùng khác. Chưa ghi dữ liệu. Sao lưu trước khi thiết lập board này thành thiết bị mới.');
    this.name = 'Esp32PartitionMismatchError';
  }
}

export type Esp32FirmwareResult = {chip: string; flashSizeBytes: number; cacheInitialized: boolean; firmware: string};

export class Esp32RuntimePendingError extends Error {
  constructor(readonly chip: string, readonly flashSizeBytes: number, readonly firmware: string, readonly stage: 'boot' | 'cache' = 'boot', reason = 'Thả nút BOOT rồi bấm Khởi động & kiểm tra ESP32.') {
    super(`Firmware đã ghi và kiểm chứng xong. ${stage === 'cache' ? 'ESP32 đã khởi động nhưng chưa hoàn tất khởi tạo cache.' : 'Chưa xác nhận ESP32 khởi động.'} ${reason}`);
    this.name = 'Esp32RuntimePendingError';
  }
}

type RuntimeReceipt = {chipMac: string; flashSizeBytes: number; releaseId: string; hashes: string; allowCacheReplace: boolean};
const runtimeReceipts = new WeakMap<Esp32SerialPort, RuntimeReceipt>();
export function forgetEsp32RuntimeReceipt(port: Esp32SerialPort | null) { if (port) runtimeReceipts.delete(port); }

export interface Esp32FlashBackup {
  readonly data: Uint8Array;
  readonly chip: string;
  readonly chipMac: string;
  readonly flashSizeBytes: number;
  readonly sha256: string;
  readonly partitionSha256: string;
  readonly createdAt: string;
}

export interface Esp32NewDeviceOptions {
  mode: 'new-device' | 'preserve-configuration';
  backup: Esp32FlashBackup;
  confirmReplace: boolean;
}

type VerifiedBackup = {
  port: Esp32SerialPort;
  data: Uint8Array;
  chipMac: string;
  flashSizeBytes: number;
  sha256: string;
  md5: string;
};
// Dumps contain credentials and journals. Keep them only in browser memory;
// require the exact dump returned by this session, never an uploaded object.
const verifiedBackups = new WeakMap<Esp32FlashBackup, VerifiedBackup>();

const activePorts = new WeakSet<Esp32SerialPort>();
const defaultVersion = '2.1.3';
const encoder = new TextEncoder();
const flashSlots = [
  {name: 'bootloader.bin', offset: 0x1000, maxSize: 0x7000},
  {name: 'partitions.bin', offset: 0x8000, maxSize: 0x1000},
  {name: 'boot_app0.bin', offset: 0xe000, maxSize: 0x2000},
  {name: 'firmware.bin', offset: 0x10000, maxSize: 0x300000},
] as const;

export function getEsp32SerialSupport(): {supported: boolean; reason: string} {
  if (!globalThis.isSecureContext) {
    return {supported: false, reason: 'Mở trang bằng HTTPS hoặc localhost để kết nối ESP32 qua USB.'};
  }
  if (typeof navigator === 'undefined' || !('serial' in navigator)) {
    return {supported: false, reason: 'Dùng Chrome hoặc Edge trên máy tính có cổng USB để nạp ESP32.'};
  }
  return {supported: true, reason: ''};
}

// Call directly in the button's click handler, before the first await. The
// browser requires a user gesture to display the serial-port chooser.
export function requestEsp32Port(): Promise<Esp32SerialPort> {
  const support = getEsp32SerialSupport();
  if (!support.supported) return Promise.reject(new Error(support.reason));
  const serial = (navigator as unknown as {serial: {requestPort(): Promise<Esp32SerialPort>}}).serial;
  return serial.requestPort();
}

export function validateEsp32ProvisionConfig(config: Esp32ProvisionConfig): string | null {
  const ssidBytes = encoder.encode(config.ssid);
  if (!ssidBytes.length || ssidBytes.length > 32 || /[\x00-\x1f\x7f]/.test(config.ssid)) {
    return 'Tên Wi-Fi cần từ 1 đến 32 byte và không chứa ký tự điều khiển.';
  }
  const pass = config.password;
  if (pass && (pass.length < 8 || pass.length > 64 || /[^\x20-\x7e]/.test(pass) || (pass.length === 64 && !/^[\da-f]{64}$/i.test(pass)))) {
    return 'Mật khẩu Wi-Fi cần 8–63 ký tự ASCII, hoặc đúng 64 ký tự hex; để trống cho mạng mở.';
  }
  if (!/^[\da-f]{64}$/i.test(config.token)) return 'Token ESP32 cần đúng 64 ký tự hex. Dùng token đã cấp cho đúng trạm.';
  const command = `CONFIG ${JSON.stringify({ssid: config.ssid, password: pass, token: config.token})}\n`;
  if (encoder.encode(command).length > 512) return 'Cấu hình vượt quá giới hạn của ESP32.';
  return null;
}

function lockPort(port: Esp32SerialPort) {
  if (activePorts.has(port)) throw new Error('ESP32 đang có thao tác USB. Chờ thao tác hiện tại hoàn tất.');
  activePorts.add(port);
}

function status(callbacks: Esp32Callbacks, text: string) {
  callbacks.onStatus?.(text);
  callbacks.onLog?.(text);
}

async function sha256(data: Uint8Array): Promise<string> {
  const bytes = new Uint8Array(data);
  const digest = await crypto.subtle.digest('SHA-256', bytes.buffer);
  return [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('');
}

function allBlank(data: Uint8Array) { return data.every(value => value === 0xff); }

function partitionEntriesLength(data: Uint8Array): number | null {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  for (let index = 0; index + 32 <= Math.min(data.length, 0xc00); index += 32) {
    const magic = view.getUint16(index, true);
    if (magic === 0x50aa) continue;
    if (index === 0) return null;
    // Validate any MD5 record, then compare all entries. Older tables may omit
    // the checksum. Bytes after its record/terminator are unused sector padding.
    if (magic === 0xebeb) {
      const checksum = [...data.subarray(index + 16, index + 32)].map(value => value.toString(16).padStart(2, '0')).join('');
      return data.subarray(index + 2, index + 16).every(value => value === 0xff) && checksum === md5(data.subarray(0, index)) ? index : null;
    }
    if (magic === 0xffff) return index;
    return null;
  }
  return null;
}

function partitionsMatch(previous: Uint8Array, expected: Uint8Array): boolean {
  const length = partitionEntriesLength(expected);
  return length !== null && partitionEntriesLength(previous) === length && expected.subarray(0, length).every((value, index) => previous[index] === value);
}

function requireClassic(loader: ESPLoader) {
  if (loader.chip.CHIP_NAME !== 'ESP32' || !loader.IS_STUB || loader.secureDownloadMode) {
    throw new Error('Chỉ hỗ trợ ESP32 Classic có Bluetooth. Không nạp gói này cho ESP32-S3/C3 hoặc chip bị khóa.');
  }
}

async function readChipMac(loader: ESPLoader): Promise<string> {
  const value = (await loader.chip.readMac(loader)).toLowerCase();
  if (!/^(?:[\da-f]{2}:){5}[\da-f]{2}$/.test(value) || value === '00:00:00:00:00:00' || value === 'ff:ff:ff:ff:ff:ff') {
    throw new Error('Không xác minh được MAC của ESP32. Đã dừng trước khi ghi.');
  }
  return value;
}

export async function backupEsp32(port: Esp32SerialPort, callbacks: Esp32Callbacks = {}): Promise<Esp32FlashBackup> {
  lockPort(port);
  let transport: Transport | undefined;
  let loader: ESPLoader | undefined;
  let detected = false;
  let backupComplete = false;
  try {
    transport = new Esp32LoaderTransport(port as ConstructorParameters<typeof Transport>[0], false);
    loader = createLoader(transport);
    status(callbacks, 'Đang nhận diện ESP32 để sao lưu toàn bộ flash. Nếu cần, giữ BOOT…');
    const chip = await loader.main();
    detected = true;
    requireClassic(loader);
    const chipMac = await readChipMac(loader);
    const size = await loader.detectFlashSize();
    const flashSizeBytes = size ? loader.flashSizeBytes(size as FlashSizeValues) : 0;
    if (!Number.isInteger(flashSizeBytes) || flashSizeBytes < 4194304 || flashSizeBytes > 16777216) {
      throw new Error('Sao lưu chỉ hỗ trợ ESP32 Classic có flash được xác minh từ 4 đến 16 MB. Chưa ghi dữ liệu.');
    }
    status(callbacks, 'Đang sao lưu toàn bộ flash qua USB. Giữ nguyên cáp và trang này…');
    callbacks.onProgress?.(0);
    const data = new Uint8Array(flashSizeBytes);
    // Finish and verify each chunk's trailing MD5 frame before issuing another
    // command. esptool-js 0.7's readFlash leaves that frame in flight.
    const chunkSize = 0x40000;
    for (let offset = 0; offset < flashSizeBytes; offset += chunkSize) {
      const count = Math.min(chunkSize, flashSizeBytes - offset);
      const chunk = await readEsp32Flash(loader, transport, offset, count, (_packet, received, total) => {
        callbacks.onProgress?.(Math.min(95, ((offset + count * (total ? received / total : 0)) / flashSizeBytes) * 95));
      });
      if (chunk.length !== count) throw new Error('Sao lưu flash chưa đủ dữ liệu. Chưa ghi dữ liệu; kết nối lại rồi sao lưu lại.');
      data.set(chunk, offset);
    }
    status(callbacks, 'Đang đối chiếu bản sao lưu với toàn bộ flash trên ESP32…');
    const digest = md5(data);
    if ((await loader.flashMd5sum(0, flashSizeBytes)).toLowerCase() !== digest) {
      throw new Error('Bản sao lưu không khớp flash trên ESP32. Chưa ghi dữ liệu; sao lưu lại trước khi nạp.');
    }
    const hash = await sha256(data);
    const backup: Esp32FlashBackup = Object.freeze({data, chip, chipMac, flashSizeBytes, sha256: hash, partitionSha256: await sha256(data.subarray(0x8000, 0x9000)), createdAt: new Date().toISOString()});
    verifiedBackups.set(backup, {port, data, chipMac, flashSizeBytes, sha256: hash, md5: digest});
    backupComplete = true;
    callbacks.onProgress?.(100);
    status(callbacks, 'Đã sao lưu và kiểm chứng toàn bộ flash. Lưu file vào máy trước khi thiết lập thiết bị mới.');
    return backup;
  } catch (error) {
    if (error instanceof Error && /Serial data stream stopped|No serial data received|Invalid head of packet|Invalid SLIP escape|Failed to read memory|timed? ?out|đọc flash qua USB|dữ liệu flash qua USB|Dữ liệu đọc qua USB|mã kiểm chứng flash/i.test(error.message)) {
      throw new Error('Sao lưu bị gián đoạn khi đọc dữ liệu qua USB. Chưa ghi hoặc xóa flash. Rút/cắm lại cáp, chọn lại cổng USB rồi sao lưu lại. Nếu còn lỗi, thử cáp truyền dữ liệu hoặc cổng USB khác và đóng ứng dụng đang dùng cổng COM.');
    }
    throw error;
  } finally {
    // Leave a successful backup in the loader while the user saves/acknowledges
    // it. Rebooting the old app could change NVS and invalidate the saved dump.
    if (loader && detected && !backupComplete) await loader.after('hard_reset').catch(() => {});
    if (transport) await transport.disconnect().catch(() => {});
    activePorts.delete(port);
  }
}

async function requireVerifiedBackup(port: Esp32SerialPort, options: Esp32NewDeviceOptions): Promise<VerifiedBackup> {
  const verified = verifiedBackups.get(options.backup);
  if (!['new-device', 'preserve-configuration'].includes(options.mode) || options.confirmReplace !== true || !verified || verified.port !== port || verified.data !== options.backup.data || verified.sha256 !== options.backup.sha256 || verified.chipMac !== options.backup.chipMac || verified.flashSizeBytes !== options.backup.flashSizeBytes || verified.data.length !== verified.flashSizeBytes) {
    throw new Error('Cần bản sao lưu đã kiểm chứng của đúng cổng USB và xác nhận thiết lập thành thiết bị mới. Chưa ghi dữ liệu.');
  }
  if ((await sha256(verified.data)) !== verified.sha256) {
    throw new Error('Bản sao lưu trong bộ nhớ đã thay đổi. Chưa ghi dữ liệu; sao lưu lại trước khi nạp.');
  }
  return verified;
}

function partitionRegions(data: Uint8Array, scheme: FirmwareRelease['partitionScheme'] = 'huge_app') {
  const regions: {type: number; subtype: number; offset: number; size: number; label: string}[] = [];
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  for (let index = 0; index + 32 <= data.length; index += 32) {
    if (view.getUint16(index, true) !== 0x50aa) break;
    const label = new TextDecoder().decode(data.slice(index + 12, index + 28)).replace(/\0.*$/, '');
    regions.push({type: data[index + 2], subtype: data[index + 3], offset: view.getUint32(index + 4, true), size: view.getUint32(index + 8, true), label});
  }
  const nvs = regions.find(region => region.label === 'nvs' && region.type === 1 && region.subtype === 2);
  const app = regions.find(region => region.label === 'app0' && region.type === 0);
  const cache = regions.find(region => region.label === 'spiffs' && region.type === 1 && region.subtype === 0x82);
  const updater = regions.find(region => region.label === 'updater' && region.type === 0 && region.subtype === 0x11);
  if (!nvs || nvs.offset !== 0x9000 || nvs.size !== 0x5000 || !app || app.offset !== 0x10000 || app.subtype !== 0x10 || app.size !== (scheme === 'huge_app' ? 0x300000 : 0x1e0000) || !cache || cache.offset !== 0x310000 || cache.size !== 0xe0000 || scheme === 'ph_recovery' && (!updater || updater.offset !== 0x1f0000 || updater.size !== 0x120000)) {
    throw new Error('Bảng phân vùng firmware không đúng cấu hình đã kiểm chứng; dừng để bảo vệ dữ liệu trạm.');
  }
  return {nvs, app, cache};
}

async function validateFirmware(release: FirmwareRelease, files: FirmwareFlashFile[]) {
  const recovery = release.partitionScheme === 'ph_recovery';
  const slots = recovery ? [...flashSlots.map(slot => slot.name === 'firmware.bin' ? { ...slot, maxSize: 0x1e0000 } : slot), { name: 'recovery.bin', offset: 0x1f0000, maxSize: 0x120000 }] : flashSlots;
  if (release.chipFamily !== 'ESP32' || release.flashSizeBytes !== 4194304 || release.flashMode !== 'dio' || release.flashFrequency !== '40m' || !['huge_app', 'ph_recovery'].includes(release.partitionScheme) || release.preserveNvs !== true || release.files.length !== slots.length || files.length !== slots.length) {
    throw new Error('Gói firmware không đúng ESP32 Classic 4 MB đã kiểm chứng.');
  }
  const ordered: FirmwareFlashFile[] = [];
  for (const slot of slots) {
    const manifest = release.files.find(file => file.name === slot.name && file.offset === slot.offset);
    const matches = files.filter(file => file.name === slot.name && file.offset === slot.offset);
    const file = matches[0];
    if (!manifest || matches.length !== 1 || !file || !(file.data instanceof Uint8Array) || !Number.isInteger(file.size) || file.size <= 0 || file.size > slot.maxSize || file.data.length !== file.size || manifest.size !== file.size || !/^[\da-f]{64}$/i.test(manifest.sha256) || manifest.sha256.toLowerCase() !== file.sha256.toLowerCase()) {
      throw new Error('File firmware hoặc địa chỉ nạp không đúng gói đã công bố.');
    }
    if ((await sha256(file.data)) !== manifest.sha256.toLowerCase()) throw new Error(`File ${slot.name} sai SHA-256. Tải lại gói firmware trước khi nạp.`);
    ordered.push(file);
  }
  // Preserve every verified byte. esptool-js's image-parameter patching can also
  // rewrite the image digest, so writeFlash below uses keep for all parameters.
  const header = ordered[0].data;
  if (header.length < 24 || header[0] !== 0xe9 || header[2] !== 2 || header[3] !== 0x20) {
    throw new Error('Bootloader không đúng chế độ DIO, 40 MHz, 4 MB.');
  }
  const regions = partitionRegions(ordered[1].data, release.partitionScheme);
  return {ordered, regions};
}

function createLoader(transport: Transport): ESPLoader {
  return new ESPLoader({transport, baudrate: 115200, romBaudrate: 115200, serialOptions: {bufferSize: 65536}, terminal: {clean() {}, write() {}, writeLine() {}}, resetConstructors: {hardReset: value => new Esp32FirmwareReset(value)}});
}

function firmwareHashes(files: FirmwareFlashFile[]): string {
  return files.map(file => `${file.offset}:${file.size}:${file.sha256.toLowerCase()}`).join('|');
}

export async function flashEsp32(port: Esp32SerialPort, release: FirmwareRelease, files: FirmwareFlashFile[], callbacks: Esp32Callbacks = {}, options?: Esp32NewDeviceOptions): Promise<Esp32FirmwareResult> {
  lockPort(port);
  let transport: Transport | undefined;
  let loader: ESPLoader | undefined;
  let detected = false;
  let readyForRuntime = false;
  let chip = '';
  let flashSizeBytes = 0;
  let blankCache = false;
  try {
    runtimeReceipts.delete(port);
    const backup = options ? await requireVerifiedBackup(port, options) : undefined;
    status(callbacks, 'Đang kiểm tra SHA-256 của gói firmware…');
    const {ordered, regions} = await validateFirmware(release, files);
    transport = new Esp32LoaderTransport(port as ConstructorParameters<typeof Transport>[0], false);
    // Suppress the library's raw terminal output; all UI logs are fixed status
    // messages, so device output or credentials cannot reach persisted logs.
    loader = createLoader(transport);
    status(callbacks, 'Đang nhận diện chip. Nếu cần, giữ BOOT khi kết nối…');
    chip = await loader.main();
    detected = true;
    requireClassic(loader);
    const size = await loader.detectFlashSize();
    flashSizeBytes = size ? loader.flashSizeBytes(size as FlashSizeValues) : 0;
    if (flashSizeBytes < release.flashSizeBytes) throw new Error('Không xác minh được flash tối thiểu 4 MB. Đã dừng trước khi ghi.');
    const chipMac = await readChipMac(loader);
    status(callbacks, 'Đang kiểm tra phân vùng để giữ Wi-Fi, token và journal…');
    const previousTable = await readEsp32Flash(loader, transport, 0x8000, 0x1000);
    if (previousTable.length !== 0x1000) throw new Error('Không đọc đủ bảng phân vùng ESP32. Đã dừng trước khi ghi.');
    if (backup) {
      status(callbacks, 'Đang đối chiếu đúng ESP32 và flash với bản sao lưu đã lưu…');
      if (flashSizeBytes !== backup.flashSizeBytes || chipMac !== backup.chipMac || (await loader.flashMd5sum(0, flashSizeBytes)).toLowerCase() !== backup.md5) {
        throw new Error('ESP32 hoặc flash đã thay đổi sau khi sao lưu. Chưa ghi dữ liệu; sao lưu lại đúng board trước khi nạp.');
      }
    }
    if (!allBlank(previousTable) && !partitionsMatch(previousTable, ordered[1].data)) {
      let legacy = false;
      try { partitionRegions(previousTable); legacy = release.partitionScheme === 'ph_recovery'; } catch { /* Unknown layouts require the existing new-board flow. */ }
      if (options?.mode === 'preserve-configuration' && !legacy) throw new Error('Không phải cấu hình trạm cũ được hỗ trợ. Chưa ghi dữ liệu.');
      if (!backup) throw new Esp32PartitionMismatchError(legacy);
    }
    if (backup && options?.mode === 'new-device') {
      status(callbacks, 'Đang xóa cấu hình và journal cũ để thiết lập thành thiết bị mới…');
      await loader.writeFlash({
        fileArray: [{address: regions.nvs.offset, data: new Uint8Array(regions.nvs.size).fill(0xff)}],
        flashSize: 'keep', flashMode: 'keep', flashFreq: 'keep', eraseAll: false, compress: true,
        calculateMD5Hash: data => md5(data),
      });
    } else {
      const cacheDigest = await loader.flashMd5sum(regions.cache.offset, regions.cache.size);
      blankCache = cacheDigest.toLowerCase() === md5(new Uint8Array(regions.cache.size).fill(0xff));
    }
    status(callbacks, 'Đang nạp firmware qua USB. Giữ nguyên cáp và trang này…');
    const totalSize = ordered.reduce((sum, file) => sum + file.size, 0);
    await loader.writeFlash({
      fileArray: ordered.map(file => ({address: file.offset, data: file.data})),
      flashSize: 'keep', flashMode: 'keep', flashFreq: 'keep', eraseAll: false, compress: true,
      calculateMD5Hash: data => md5(data),
      reportProgress: (index, written, total) => {
        const completed = ordered.slice(0, index).reduce((sum, file) => sum + file.size, 0);
        callbacks.onProgress?.(Math.min(95, ((completed + ordered[index].size * (total ? written / total : 0)) / totalSize) * 95));
      },
    });
    runtimeReceipts.set(port, {chipMac, flashSizeBytes, releaseId: release.id, hashes: firmwareHashes(ordered), allowCacheReplace: options?.mode === 'new-device'});
    status(callbacks, 'Đã ghi và kiểm chứng từng file trên flash. Đang khởi động ESP32…');
    // Reopen the runtime stream and attach its reader before releasing reset.
    // This avoids losing the boot response or rebooting once before reopening.
    readyForRuntime = true;
  } finally {
    if (loader && detected && !readyForRuntime) await loader.after('hard_reset').catch(() => {});
    if (transport) await transport.disconnect().catch(() => {});
    if (!readyForRuntime) activePorts.delete(port);
  }
  try {
    const runtime = await completeFirmwareRuntime(port, release.version, blankCache || options?.mode === 'new-device', callbacks, true);
    runtimeReceipts.delete(port);
    return {chip, flashSizeBytes, ...runtime};
  } catch (error) {
    const failure = error instanceof Esp32RuntimeStageError ? error : undefined;
    throw new Esp32RuntimePendingError(chip, flashSizeBytes, release.version, failure?.stage, failure?.message);
  }
}

/** Check the installed images and boot them without writing firmware or NVS. */
export async function checkEsp32Firmware(port: Esp32SerialPort, release: FirmwareRelease, files: FirmwareFlashFile[], callbacks: Esp32Callbacks = {}): Promise<Esp32FirmwareResult> {
  lockPort(port);
  let transport: Transport | undefined;
  let loader: ESPLoader | undefined;
  let detected = false;
  let readyForRuntime = false;
  let chip = '';
  let flashSizeBytes = 0;
  let initializeCache = false;
  try {
    status(callbacks, 'Đang kiểm tra gói firmware và firmware đã ghi trên ESP32…');
    const {ordered, regions} = await validateFirmware(release, files);
    transport = new Esp32LoaderTransport(port as ConstructorParameters<typeof Transport>[0], false);
    loader = createLoader(transport);
    chip = await loader.main(); detected = true; requireClassic(loader);
    const size = await loader.detectFlashSize();
    flashSizeBytes = size ? loader.flashSizeBytes(size as FlashSizeValues) : 0;
    if (flashSizeBytes < release.flashSizeBytes) throw new Error('Không xác minh được flash tối thiểu 4 MB. Chưa ghi dữ liệu.');
    const chipMac = await readChipMac(loader);
    for (let index = 0; index < ordered.length; index++) {
      const file = ordered[index];
      if ((await loader.flashMd5sum(file.offset, file.size)).toLowerCase() !== md5(file.data)) {
        throw new Error(`Firmware đã ghi chưa khớp ${file.name} của bản ${release.version}. Chưa ghi dữ liệu; cần nạp đúng bản firmware.`);
      }
      callbacks.onProgress?.((index + 1) / ordered.length * 90);
    }
    const receipt = runtimeReceipts.get(port);
    const sameReceipt = receipt?.chipMac === chipMac && receipt.flashSizeBytes === flashSizeBytes && receipt.releaseId === release.id && receipt.hashes === firmwareHashes(ordered);
    if (!sameReceipt) runtimeReceipts.delete(port);
    const blankCache = (await loader.flashMd5sum(regions.cache.offset, regions.cache.size)).toLowerCase() === md5(new Uint8Array(regions.cache.size).fill(0xff));
    initializeCache = blankCache || (sameReceipt && receipt?.allowCacheReplace === true);
    callbacks.onProgress?.(95);
    status(callbacks, 'Đã đối chiếu đủ bốn file firmware. Đang khởi động và chờ ESP32 phản hồi…');
    readyForRuntime = true;
  } finally {
    if (loader && detected && !readyForRuntime) await loader.after('hard_reset').catch(() => {});
    if (transport) await transport.disconnect().catch(() => {});
    if (!readyForRuntime) activePorts.delete(port);
  }
  try {
    const runtime = await completeFirmwareRuntime(port, release.version, initializeCache, callbacks, true);
    runtimeReceipts.delete(port);
    return {chip, flashSizeBytes, ...runtime};
  } catch (error) {
    const failure = error instanceof Esp32RuntimeStageError ? error : undefined;
    throw new Esp32RuntimePendingError(chip, flashSizeBytes, release.version, failure?.stage, failure?.message);
  }
}

class Esp32RuntimeStageError extends Error {
  constructor(readonly stage: 'boot' | 'cache', reason: string) { super(reason); }
}

async function completeFirmwareRuntime(port: Esp32SerialPort, version: string, initializeCache: boolean, callbacks: Esp32Callbacks, alreadyLocked: boolean) {
  // Existing stations initialize only a proven blank cache. The explicit new
  // device path permits replacement after a verified full backup and NVS reset.
  let stage: 'boot' | 'cache' = 'boot';
  try { return await withSerialSession(port, callbacks, async session => {
    await session.info(version);
    let cacheInitialized = false;
    if (initializeCache) {
      stage = 'cache';
      status(callbacks, 'Đang khởi tạo vùng cache đã kiểm chứng…');
      const response = await session.command('CACHE_INIT', line => /^CACHE_INIT ok=/.test(line), 20000);
      if (!/^CACHE_INIT ok=1\b/.test(response)) throw new Error('Firmware đã nạp nhưng chưa khởi tạo được cache. Chưa thể dùng trạm để in/phát loa.');
      cacheInitialized = true;
    }
    callbacks.onProgress?.(100);
    status(callbacks, `ESP32 đã chạy firmware ${version}.`);
    return {cacheInitialized, firmware: version};
  }, alreadyLocked, true);
  } catch (error) {
    const reason = error instanceof Error && /^(ESP32|Firmware|Mất kết nối USB|Không gửi được lệnh|Không mở được luồng)/.test(error.message) ? error.message : 'Không hoàn tất được kết nối USB. Kết nối lại rồi bấm Khởi động & kiểm tra ESP32.';
    throw new Esp32RuntimeStageError(stage, reason);
  }
}

type RuntimeInfo = {firmware: string; wifiConnected: boolean};
class Esp32ResponseTimeoutError extends Error {
  constructor() { super('ESP32 chưa phản hồi qua USB. Thả nút BOOT, nhấn EN rồi kiểm tra lại.'); }
}
function parseRuntime(line: string): RuntimeInfo | null {
  const match = /^RUNTIME firmware=([^\s]+)\s.*\bwifi=(0|1)\b/.exec(line);
  return match ? {firmware: match[1], wifiConnected: match[2] === '1'} : null;
}

class SerialSession {
  private readonly reader: ReadableStreamDefaultReader<Uint8Array>;
  private readonly writer: WritableStreamDefaultWriter<Uint8Array>;
  private readonly loop: Promise<void>;
  private readonly pending = new Set<{accept: (line: string) => boolean; resolve: (line: string) => void; reject: (error: Error) => void}>();
  private closed = false;
  private failure: Error | null = null;
  private lastRuntime: RuntimeInfo | null = null;
  constructor(port: Esp32SerialPort) {
    if (!port.readable || !port.writable) throw new Error('Không mở được luồng dữ liệu USB của ESP32.');
    this.reader = port.readable.getReader();
    this.writer = port.writable.getWriter();
    this.loop = this.readLines();
  }
  private async readLines() {
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (!this.closed) {
        const {value, done} = await this.reader.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        if (buffer.length > 4096) buffer = '';
        for (const item of lines) {
          const line = item.replace(/\r$/, '');
          const runtime = parseRuntime(line);
          if (runtime) this.lastRuntime = runtime;
          for (const waiter of this.pending) {
            if (waiter.accept(line)) { this.pending.delete(waiter); waiter.resolve(line); }
          }
        }
      }
      if (!this.closed) this.fail(new Error('ESP32 đã ngắt cáp USB. Kết nối lại và kiểm tra trạng thái trạm.'));
    } catch {
      if (!this.closed) this.fail(new Error('Mất kết nối USB với ESP32. Kết nối lại và kiểm tra trạng thái trạm.'));
    } finally {
      this.reader.releaseLock();
    }
  }
  private fail(error: Error) {
    this.failure = error;
    for (const waiter of this.pending) waiter.reject(error);
    this.pending.clear();
  }
  async command(command: string, accept: (line: string) => boolean, timeout = 12000): Promise<string> {
    if (this.failure) throw this.failure;
    let waiter!: {accept: (line: string) => boolean; resolve: (line: string) => void; reject: (error: Error) => void};
    let timer: ReturnType<typeof setTimeout> | undefined;
    const response = new Promise<string>((resolve, reject) => {
      waiter = {accept, resolve, reject};
      this.pending.add(waiter);
      timer = setTimeout(() => { this.pending.delete(waiter); reject(new Esp32ResponseTimeoutError()); }, timeout);
    });
    // Attach a handler before writing: unplugging during write rejects the
    // response immediately and must not create an unhandled rejection.
    response.catch(() => {});
    try {
      await this.writer.write(encoder.encode(command + '\n'));
      return await response;
    } catch (error) {
      if (this.failure) throw this.failure;
      if (error instanceof Error && error.message.startsWith('ESP32')) throw error;
      throw new Error('Không gửi được lệnh qua USB. Kết nối lại ESP32 và kiểm tra trạng thái trước khi thử lại.');
    } finally {
      if (timer) clearTimeout(timer);
      this.pending.delete(waiter);
    }
  }
  clearRuntime() { this.lastRuntime = null; }
  async info(expectedVersion: string, timeout = 30000): Promise<RuntimeInfo> {
    const validate = (info: RuntimeInfo) => {
      if (info.firmware !== expectedVersion) throw new Error(`ESP32 chưa chạy firmware ${expectedVersion}. Nạp đúng phiên bản trước khi lưu Wi-Fi/token.`);
      return info;
    };
    const deadline = Date.now() + timeout;
    while (Date.now() < deadline) {
      if (this.failure) throw this.failure;
      if (this.lastRuntime) return validate(this.lastRuntime);
      try {
        // Only INFO is repeated. The leading newline separates any interrupted
        // serial input, and the loop stops before secrets or CACHE_INIT are sent.
        const line = await this.command('\nINFO', candidate => parseRuntime(candidate) !== null, Math.min(1000, deadline - Date.now()));
        return validate(parseRuntime(line)!);
      } catch (error) {
        if (!(error instanceof Esp32ResponseTimeoutError)) throw error;
      }
    }
    throw new Esp32ResponseTimeoutError();
  }
  async close() {
    this.closed = true;
    this.fail(new Error('Đã đóng kết nối USB.'));
    await this.reader.cancel().catch(() => {});
    await this.loop;
    this.writer.releaseLock();
  }
}

async function withSerialSession<T>(port: Esp32SerialPort, callbacks: Esp32Callbacks, action: (session: SerialSession) => Promise<T>, alreadyLocked = false, resetFirmware = false): Promise<T> {
  if (!alreadyLocked) lockPort(port);
  let session: SerialSession | undefined;
  let opened = false;
  try {
    status(callbacks, 'Đang đọc trạng thái firmware qua USB…');
    await port.open({baudRate: 115200, bufferSize: 8192});
    opened = true;
    // Leave EN and BOOT inactive after the loader's hard reset. No firmware
    // erase, extra peripheral GPIO change, or credential logging is performed.
    session = new SerialSession(port);
    if (resetFirmware) {
      await new Esp32FirmwareReset({setSignals: (dtr, rts) => port.setSignals({dataTerminalReady: dtr, requestToSend: rts})}).reset();
      session.clearRuntime();
    } else await port.setSignals({dataTerminalReady: false, requestToSend: false});
    return await action(session);
  } finally {
    if (session) await session.close();
    if (opened) await port.close().catch(() => {});
    activePorts.delete(port);
  }
}

export async function provisionEsp32(port: Esp32SerialPort, config: Esp32ProvisionConfig, callbacks: Esp32Callbacks = {}): Promise<{firmware: string; wifiConnected: boolean; configSaved: true}> {
  const invalid = validateEsp32ProvisionConfig(config);
  if (invalid) throw new Error(invalid);
  return withSerialSession(port, callbacks, async session => {
    const version = config.firmwareVersion ?? defaultVersion;
    await session.info(version);
    status(callbacks, 'Đang lưu Wi-Fi và token trực tiếp vào ESP32…');
    const response = await session.command(`CONFIG ${JSON.stringify({ssid: config.ssid, password: config.password, token: config.token})}`, line => /^CONFIG_(SAVED|INVALID|INVALID_FIELDS|WRITE_FAIL|REFUSED|FAIL)\b/.test(line), 20000);
    if (response.startsWith('CONFIG_REFUSED')) throw new Error('ESP32 đang thực hiện tác vụ. Chờ trạm rảnh rồi lưu cấu hình lại.');
    if (response.startsWith('CONFIG_INVALID')) throw new Error('ESP32 từ chối cấu hình. Kiểm tra Wi-Fi, mật khẩu và token của đúng trạm.');
    if (!response.startsWith('CONFIG_SAVED')) throw new Error('ESP32 không lưu được đầy đủ cấu hình. Kiểm tra trạng thái trước khi thử lại.');
    status(callbacks, 'ESP32 xác nhận đã lưu cấu hình. Đang chờ khởi động lại…');
    session.clearRuntime();
    // CONFIG_SAVED is followed by a restart. Delay sending INFO until the old
    // runtime can no longer answer, then require the correct rebooted firmware.
    await new Promise(resolve => setTimeout(resolve, 600));
    const runtime = await session.info(version, 25000);
    status(callbacks, runtime.wifiConnected ? 'Đã lưu cấu hình; ESP32 xác nhận đã kết nối Wi-Fi.' : 'Đã lưu cấu hình và xác nhận firmware khởi động; đang chờ trạm kết nối Wi-Fi.');
    return {...runtime, configSaved: true};
  });
}
